import isEmpty from 'lodash/isEmpty';
import { Hits } from 'react-instantsearch';

import { Box } from '@/components/Box';
import { Col } from '@/components/Col';
import { Flex } from '@/components/Flex';
import { Grid } from '@/components/Grid';
import { AddressSection } from '@/components/MyOrders/AddressSection';
import { Typography } from '@/components/Typography';
import type { StoreHit } from '@/integrations/algolia/types';
import type { PaymentRecord } from '@/lib/payment/types';
import * as CM from '@/messages/common';
import * as FM from '@/messages/forms';
import * as OM from '@/messages/order';
import * as PM from '@/messages/payment';

import { BoutiqueOpeningHours } from '../components/BoutiqueOpeningHours';
import { StoresSearch } from '../components/StoresSearch';
import { useOrderDetailsContext } from '../OrderDetailsContext';

import * as S from '../OrderDetailsView.styles';

type StoreHitComponentProps = Readonly<{ hit: StoreHit }>;

function StorePhoneHit({ hit }: StoreHitComponentProps) {
  return (
    <S.StyledLink key={hit.objectID} href={`tel:${hit.phone}`}>
      {hit.phone}
    </S.StyledLink>
  );
}

function StoreEmailHit({ hit }: StoreHitComponentProps) {
  return <Typography key={hit.objectID}>{hit.emails}</Typography>;
}

export function OrderAddressesSection() {
  const {
    billingTitleLabel,
    formatMessage,
    isHomeDelivery,
    isMobileView,
    order,
    paymentRecords,
    shippingTitleLabel,
    storeId,
    userPhoneNumber,
  } = useOrderDetailsContext();
  return (
    <>
      <Box backgroundColor='lightGrey' mb={2} padding={2}>
        <Typography
          data-test-id='OrderDetailsViewDeliveryInformationTitle'
          fontFamily='italianPlate'
          mb={1}
          scale='h5'
          textTransform='uppercase'
        >
          {OM.DeliveryInformation}
        </Typography>

        <Grid
          data-test-id='OrderDetailsViewDeliveryInformationGrid'
          gridAutoFlow={isMobileView ? 'row' : 'column'}
          gridGap={2}
        >
          <Col>
            <AddressSection
              address={order.shippingAddress!}
              heading={
                <Typography
                  data-test-id='OrderDetailsViewShippingAddressSection'
                  fontWeight='bold'
                >
                  {isHomeDelivery
                    ? formatMessage(CM.commonMessages.shippingAddress)
                    : order.shippingAddress?.companyName}
                </Typography>
              }
              honorificPrefix={shippingTitleLabel ?? undefined}
              showName={isHomeDelivery}
            />
            {!isHomeDelivery && (
              <>
                <Flex
                  alignItems='baseline'
                  data-test-id='OrderDetailsViewPhoneLink'
                >
                  <Typography fontWeight='bold'>
                    {formatMessage(FM.formMessages.phone)}
                  </Typography>
                  ：
                  <StoresSearch storeId={storeId}>
                    <Hits
                      hitComponent={StorePhoneHit}
                      transformItems={(items) =>
                        items.length ? [items[0]] : []
                      }
                    ></Hits>
                  </StoresSearch>
                </Flex>
                <Flex
                  alignItems='baseline'
                  data-test-id='OrderDetailsViewEmailLink'
                >
                  <Typography fontWeight='bold'>
                    {formatMessage(FM.formMessages.email)}
                  </Typography>
                  ：
                  <StoresSearch storeId={storeId}>
                    <Hits
                      hitComponent={StoreEmailHit}
                      transformItems={(items) =>
                        items.length ? [items[0]] : []
                      }
                    ></Hits>
                  </StoresSearch>
                </Flex>
              </>
            )}
          </Col>

          {isHomeDelivery && order.timeSlot && (
            <Col>
              <Typography fontWeight='bold'>
                {formatMessage(CM.commonMessages.deliverySchedule)}
              </Typography>
              <Typography data-test-id='OrderDetailsViewDeliverySchedule'>
                {order.timeSlot}
              </Typography>
            </Col>
          )}
          {!isHomeDelivery && (
            <Col>
              <Typography fontWeight='bold'>
                {formatMessage(FM.formMessages.boutiqueHours)}
              </Typography>
              <StoresSearch storeId={storeId}>
                <BoutiqueOpeningHours></BoutiqueOpeningHours>
              </StoresSearch>
            </Col>
          )}
        </Grid>
      </Box>

      {!isEmpty(paymentRecords) && (
        <Box
          backgroundColor='lightGrey'
          data-test-id='OrderDetailsViewPaymentInformationSection'
          mb={2}
          padding={2}
        >
          <Typography
            data-test-id='OrderDetailsViewPaymentInformationTitle'
            fontFamily='italianPlate'
            mb={1}
            scale='h5'
            textTransform='uppercase'
          >
            {formatMessage(PM.paymentMessages.paymentMethod)}
          </Typography>
          <Grid
            data-test-id='OrderDetailsViewPaymentInformationGrid'
            gridAutoFlow={isMobileView ? 'row' : 'column'}
            gridGap={2}
          >
            <Col>
              <S.PaymentRecords
                currency={order.total.gross.currency}
                paymentRecords={paymentRecords as PaymentRecord[]}
                showCheckIcon={false}
                showOrder={false}
              />
            </Col>
            <Col>
              <AddressSection
                address={order.billingAddress!}
                heading={
                  <Typography
                    data-test-id='OrderDetailsViewBillingAddressSection'
                    fontWeight='bold'
                  >
                    {formatMessage(CM.commonMessages.billingAddress)}
                  </Typography>
                }
                honorificPrefix={billingTitleLabel ?? undefined}
              />
            </Col>
          </Grid>
        </Box>
      )}

      <Box
        backgroundColor='lightGrey'
        data-test-id='OrderDetailsViewCustomerContactInformationSection'
        mb={2}
        padding={2}
      >
        <Typography
          data-test-id='OrderDetailsViewCustomerContactInformationTitle'
          fontFamily='italianPlate'
          mb={1}
          scale='h5'
          textTransform='uppercase'
        >
          {formatMessage(CM.commonMessages.customerContactInformation)}
        </Typography>
        <Grid data-test-id='OrderDetailsViewCustomerContactInformationGrid'>
          <Col>
            <Typography data-test-id='OrderDetailsViewCustomerEmail'>
              {order.userEmail}
            </Typography>
            {userPhoneNumber ? (
              <Typography data-test-id='OrderDetailsViewCustomerPhone'>
                {userPhoneNumber}
              </Typography>
            ) : null}
          </Col>
        </Grid>
      </Box>
    </>
  );
}
